import React, { useState, useEffect } from "react";

interface DynamicArrayProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}

const DynamicArray: React.FC<DynamicArrayProps> = ({
  label,
  items,
  onChange,
  placeholder = "Enter value",
}) => {
  const [error, setError] = useState("");
  const hasEmptyItems = items.some(item => !item.trim());

  // Clear error automatically when user fixes input
  useEffect(() => {
    if (!hasEmptyItems) {
      setError("");
    }
  }, [items]);

  const handleAdd = () => {
    if (hasEmptyItems) {
      setError(
        `Fill in all existing fields before adding a new one.`
      );
      return;
    }
    onChange([...items, ""]);
  };

  const handleUpdate = (index: number, value: string) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated);
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const isItemEmpty = (item: string) => !item.trim();

  return (
    <div className="mt-1">
      <div className="space-y-1 ">
        {items.map((item, idx) => {
          const showError = isItemEmpty(item);

          return (
            <div key={idx} className="flex gap-1 items-start group">
              <div className="flex-shrink-0 w-8 h-8 text-blue-500 rounded-lg flex items-center justify-center font-bold text-sm shadow-sm">
                {idx + 1}
              </div>

              <div className="flex-1 relative mb-3">
                <input
                  type="text"
                  required
                  minLength={10}
                  value={item}
                  onChange={(e) => handleUpdate(idx, e.target.value)}
                  placeholder={`Enter ${label}`}
                  // aria-invalid={showError}
                  className={`w-full px-4 py-1 text-sm border-2 rounded-lg transition-all focus:outline-none focus:ring-1 ${
                    showError
                      ? "border-red-400 bg-red-50 focus:ring-red-400"
                      : "border-gray-300 hover:border-gray-400 focus:ring-blue-500"
                  }`}
                />

                {showError && (
                  <div className="absolute -bottom-3.5 right-0 text-[10px] text-red-600 font-medium flex items-center gap-1 mt-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Required field
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => handleRemove(idx)}
                title="Remove item"
                className="flex-shrink-0 w-8 h-8 text-red-500 hover:text-white rounded-lg hover:bg-red-600 flex items-center justify-center font-bold shadow-sm hover:shadow-md transition-all group-hover:scale-105"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          );
        })}
      </div>

      {error && (
  <div className="mb-2 w-fit max-w-full">
    <div className="flex items-center gap-2 px-3 py-1 text-[10px] text-red-700 bg-red-50 border-l-4 border-red-500 rounded-md shadow-sm">
      <svg
        className="w-4 h-4 flex-shrink-0 text-red-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      <span>{error}</span>
    </div>
  </div>
)}


      <button
        type="button"
        onClick={handleAdd}
        className="flex items-center gap-2 px-3 py-1  border-1 border border-blue-400 text-blue-700 rounded-lg hover:from-blue-100 hover:to-indigo-100 font-semibold text-xs shadow-sm hover:shadow transition-all w-fit justify-center group"
      >
        <svg
          className="w-4 h-4 group-hover:rotate-90 transition-transform"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Add  {label}
      </button>
    </div>
  );
};

export default DynamicArray;
